<script setup>
// Turn one or more waiting players into a placed team (JOIN_SETTINGS_PLAN.md §7.6):
// ladder — bottom of the standings; league — a division (or a new one); americano —
// take over a seat in a division. Preferred partners are pre-selected as hints.
import TlDialog from '~/components/TlDialog.vue';
import UserProfilePic from '~/components/users/UserProfilePic.vue';
import {usePendingPlacements} from '~/composables/usePendingPlacements';
import {useLadderRung} from '~/composables/useLadderRung.js';
import {useAmericanoFormat} from '~/composables/useAmericanoFormat';

const props = defineProps({
  // The waiting rows the admin picked (1..playersPerTeam).
  rows: {type: Array, default: () => []},
  // Every waiting row in the competition — the first partner candidates.
  waiting: {type: Array, default: () => []},
  referenceType: {type: Number, required: true},
  referenceId: {type: String, required: true},
  clubId: {type: String, required: true},
  playersPerTeam: {type: Number, default: 1},
  // League: [{id, name}] ; americano: [{id, name, players: [{id, fullName}]}]
  divisions: {type: Array, default: () => []},
  // Ladder: the entries already on it, in order — [{ladderTeamId, name, newPosition}].
  // Lets the admin slot the new one in rather than always landing at the bottom.
  ladderEntries: {type: Array, default: () => []},
  handicapsEnabled: {type: Boolean, default: false},
  // Ladder / league: the teams already in the competition —
  // [{teamId, name, players: [{id, fullName, imageFileName}], where}]. Their players are
  // not offered as team-mates (the API refuses them), and a team that is short of
  // players can be completed instead of a new one being made.
  existingTeams: {type: Array, default: () => []},
  // Ladders that let one player be in several teams exclude nobody.
  allowMultipleTeams: {type: Boolean, default: false},
  // Americano: where to set up divisions when the cycle has none yet (route location).
  manageRoute: {type: Object, default: null},
  // Already on the manage page: the button opens the division editor there instead of navigating.
  setupHere: {type: Boolean, default: false},
  // Americano: the cycle's entry fee (optional — null/absent = don't mention money).
  entryFeeMinor: {type: Number, default: null},
  entryFeeCurrency: {type: String, default: null},
});
const open = defineModel({type: Boolean, default: false});
const emit = defineEmits(['placed', 'setup']);

const {place, actionPending, actionErrors} = usePendingPlacements();
const {items: clubPlayers, getItems: getClubPlayers, listPending: playersPending} = useCrudApi('api/v2/players');
const {setAlertMessage} = useAlertService();
const {moreToJoinText} = useAmericanoFormat();

const isLadder = computed(() => props.referenceType === 1);
const {rungOrdinal} = useLadderRung();

// null = the bottom, which is where joining puts people.
const aboveLadderTeamId = ref(null);
// The rung LEADS the label. It used to trail as "Above {name} ({n})", which failed twice
// over: a bare "(2)" after a doubles pairing reads as a member count, and Vuetify
// ellipsises the trailing end, so on a long pairing the rank was the first thing cut —
// both exactly as reported. Leading with it also answers the question actually being
// asked, which is where the player LANDS, not who they displace.
const ladderSpots = computed(() => [
  ...props.ladderEntries.map(e => ({
    value: e.ladderTeamId,
    title: e.newPosition ? `${rungOrdinal(e.newPosition)} place · above ${e.name}` : `Above ${e.name}`,
  })),
  {value: null, title: 'Bottom of the ladder'},
]);
const isLeague = computed(() => props.referenceType === 2);
const isAmericano = computed(() => props.referenceType === 6);
const needed = computed(() => Math.max(0, props.playersPerTeam - props.rows.length));

const partnerIds = ref([]);
const teamName = ref('');
const divisionId = ref(null);
const newDivisionName = ref('');
const outPlayerId = ref(null);
const handicap = ref(null);
const partnerSearch = ref('');
// The short-handed team being completed, when the admin picks one instead of team-mates.
const completeTeamId = ref(null);

const NEW_DIVISION = '__new__';
// Americano: "an empty seat" in a division that is short of 4, 8 or 12 (nobody is replaced).
const EMPTY_SEAT = '__empty__';

const reset = () => {
  // Preferred partners who are still waiting come pre-ticked (hint, not a rule).
  const rowPlayerIds = new Set(props.rows.map(r => r.playerId));
  const preferred = props.rows.flatMap(r => (r.preferredPartners ?? []))
      .filter(p => p.stillWaiting && !rowPlayerIds.has(p.playerId))
      .map(p => p.playerId);
  partnerIds.value = [...new Set(preferred)].slice(0, needed.value);
  teamName.value = '';
  divisionId.value = props.divisions.length === 1 ? props.divisions[0].id : null;
  newDivisionName.value = '';
  outPlayerId.value = null;
  handicap.value = null;
  partnerSearch.value = '';
  completeTeamId.value = null;
  actionErrors.value = null;
  // Back to the bottom every time. Left alone, "Above Bob" chosen for one
  // player was still selected for the next, quietly slotting them mid-ladder.
  aboveLadderTeamId.value = null;
};

watch(open, async (v) => {
  if (!v) return;
  reset();
  if (needed.value > 0 && !(clubPlayers.value?.length)) {
    await getClubPlayers(1, 1000, {clubIds: [props.clubId]});
  }
});

// Partner candidates: the other waiting players first (marked), then club members.
const candidates = computed(() => {
  const rowIds = new Set(props.rows.map(r => r.playerId));
  const waitingIds = new Set();
  const fromWaiting = props.waiting
      .filter(w => !rowIds.has(w.playerId))
      .map(w => {
        waitingIds.add(w.playerId);
        const mutual = props.rows.some(r => (r.preferredPartners ?? []).some(p => p.playerId === w.playerId && p.isMutual));
        const picked = props.rows.some(r => (r.preferredPartners ?? []).some(p => p.playerId === w.playerId));
        return {id: w.playerId, name: w.fullName, imageFileName: w.imageFileName, waiting: true, mutual, picked};
      });
  const fromClub = (clubPlayers.value ?? [])
      .filter(p => !rowIds.has(p.id) && !waitingIds.has(p.id) && !takenIds.value.has(p.id))
      .map(p => ({id: p.id, name: p.fullName, imageFileName: p.imageFileName, waiting: false}));
  const all = [...fromWaiting, ...fromClub];
  const q = partnerSearch.value.trim().toLowerCase();
  return q ? all.filter(c => c.name?.toLowerCase().includes(q)) : all;
});

// Already in the competition: never a valid team-mate, so never offered.
const takenIds = computed(() => props.allowMultipleTeams
    ? new Set()
    : new Set(props.existingTeams.flatMap(t => (t.players ?? []).map(p => p.id))));

// Teams an admin left short — exactly as many missing as are being placed now.
const shortTeams = computed(() => props.playersPerTeam <= 1 ? [] : props.existingTeams
    .filter(t => (t.players ?? []).length > 0
        && props.playersPerTeam - t.players.length === props.rows.length));
const completing = computed(() => shortTeams.value.find(t => t.teamId === completeTeamId.value) ?? null);
const toggleComplete = (team) => {
  completeTeamId.value = completeTeamId.value === team.teamId ? null : team.teamId;
  if (completeTeamId.value) partnerIds.value = [];
};
const playerNames = (team) => (team.players ?? []).map(p => p.fullName).join(' & ');

const togglePartner = (id) => {
  completeTeamId.value = null;
  if (partnerIds.value.includes(id)) {
    partnerIds.value = partnerIds.value.filter(x => x !== id);
  } else if (partnerIds.value.length < needed.value) {
    partnerIds.value = [...partnerIds.value, id];
  }
};

const outCandidates = computed(() => {
  const d = props.divisions.find(x => x.id === divisionId.value);
  return d?.players ?? [];
});
// How many an americano division of this many seated players is short, if any
// (same rule as the app: up to the next whist size, never past twelve).
const openSeatsFor = (seated) => {
  if (seated >= 12 || seated === 4 || seated === 8) return 0;
  return seated < 4 ? 4 - seated : seated < 8 ? 8 - seated : 12 - seated;
};
const openSeats = computed(() =>
    isAmericano.value && divisionId.value && divisionId.value !== NEW_DIVISION
        ? openSeatsFor(outCandidates.value.length) : 0);
const seatItems = computed(() => [
  ...(openSeats.value > 0 ? [{id: EMPTY_SEAT, fullName: 'An empty seat'}] : []),
  ...outCandidates.value,
]);
const fillsEmptySeat = computed(() => outPlayerId.value === EMPTY_SEAT);
// A seat belongs to one division, and americano divisions are numbered.
watch(divisionId, (id) => {
  outPlayerId.value = null;
  if (id === NEW_DIVISION && isAmericano.value && !newDivisionName.value.trim()) {
    newDivisionName.value = `Division ${props.divisions.length + 1}`;
  }
});

const canSubmit = computed(() => {
  if (actionPending.value) return false;
  // Completing a team: who, where and with whom are all already decided.
  if (completing.value) return true;
  if (partnerIds.value.length !== needed.value) return false;
  if (isLeague.value) {
    return divisionId.value === NEW_DIVISION ? newDivisionName.value.trim().length > 0 : !!divisionId.value;
  }
  if (isAmericano.value) {
    return divisionId.value === NEW_DIVISION
        ? newDivisionName.value.trim().length > 0
        : !!divisionId.value && !!outPlayerId.value;
  }
  return true;
});

const submit = async () => {
  const payload = {
    pendingPlacementIds: props.rows.map(r => r.id),
    partnerPlayerIds: partnerIds.value,
    teamName: teamName.value?.trim() || null,
    handicap: props.handicapsEnabled && handicap.value !== null && handicap.value !== '' ? Number(handicap.value) : null,
  };
  if (completing.value) {
    payload.partnerPlayerIds = [];
    payload.completeTeamId = completing.value.teamId;
    const res = await place(payload);
    if (res) {
      setAlertMessage(`Added to ${completing.value.name || playerNames(completing.value)}`);
      open.value = false;
      emit('placed', res);
    }
    return;
  }
  if (isLeague.value) {
    if (divisionId.value === NEW_DIVISION) payload.newDivisionName = newDivisionName.value.trim();
    else payload.leagueCycleDivisionId = divisionId.value;
  }
  if (isLadder.value && aboveLadderTeamId.value) {
    payload.aboveLadderTeamId = aboveLadderTeamId.value;
  }
  const startsDivision = isAmericano.value && divisionId.value === NEW_DIVISION;
  if (startsDivision) {
    // A new division is short from its first seat: nobody is replaced.
    payload.newDivisionName = newDivisionName.value.trim();
  } else if (isAmericano.value) {
    payload.americanoCycleDivisionId = divisionId.value;
    // An empty seat sends no out-player: the API seats them in the gap.
    payload.americanoOutPlayerId = fillsEmptySeat.value ? null : outPlayerId.value;
  }
  const res = await place(payload);
  if (res) {
    setAlertMessage(isLadder.value
        ? (aboveLadderTeamId.value ? 'Placed on the ladder' : 'Placed at the bottom of the ladder')
        : isLeague.value ? 'Placed in the division'
        : startsDivision ? `Seated in ${payload.newDivisionName}. Its matches start when it has 4 players`
        : 'Seated in the division');
    open.value = false;
    emit('placed', res);
  }
};

const rowNames = computed(() => props.rows.map(r => r.fullName).join(' & '));

// Give seat = a swap: the outgoing player leaves the cycle but keeps any entry
// fee they owe (Grant, 2026-09-21 — same as a refund); the newcomer owes theirs.
const {money} = usePaymentsDisplay();
const outPlayerName = computed(() => outCandidates.value.find(p => p.id === outPlayerId.value)?.fullName ?? null);
const seatFeeNote = computed(() => {
  if (!isAmericano.value || !outPlayerName.value) return null;
  const leaves = `${outPlayerName.value} leaves this cycle and gives up their seat.`;
  if (!props.entryFeeMinor) return leaves;
  const fee = money(props.entryFeeMinor, props.entryFeeCurrency || 'gbp');
  return `${leaves} ${rowNames.value} will owe the ${fee} entry fee. ${outPlayerName.value} still owes the ${fee} entry fee. Waive it from Payments if they shouldn't pay.`;
});
const divisionItems = computed(() => [
  ...props.divisions.map(d => {
    const open = isAmericano.value ? openSeatsFor((d.players ?? []).length) : 0;
    return {value: d.id, title: open > 0 ? `${d.name} · ${open} ${open === 1 ? 'seat' : 'seats'} open` : d.name};
  }),
  ...(isLeague.value || isAmericano.value ? [{value: NEW_DIVISION, title: 'New division…'}] : []),
]);
</script>

<template>
  <tl-dialog v-model="open" :max-width="520">
    <template #title>
      {{ isAmericano ? 'Give a seat' : isLadder ? 'Add to the ladder' : 'Add to a division' }}
    </template>

    <!-- Who -->
    <div class="ppd-who">
      <div v-for="r in rows" :key="r.id" class="ppd-person">
        <user-profile-pic :user="{imageFileName: r.imageFileName, fullName: r.fullName}" size="small"/>
        <div class="min-w-0">
          <div class="ppd-name">{{ r.fullName }}</div>
          <div v-if="r.message" class="ppd-msg">“{{ r.message }}”</div>
        </div>
        <span class="tl-chip tl-chip--warn ppd-chip"><v-icon size="12" icon="mdi-account-clock-outline" class="mr-1"/>Waiting</span>
      </div>
    </div>

    <!-- A team already in, short of players: complete it rather than make another -->
    <template v-if="shortTeams.length">
      <div class="tl-label mt-4">
        {{ shortTeams.length === 1 ? 'A team is missing a player' : 'Teams missing a player' }}
        <span class="ppd-sub"> · add {{ rows.length === 1 ? 'them' : 'these players' }} to it</span>
      </div>
      <div class="ppd-cands ppd-cands--short mt-1">
        <button v-for="t in shortTeams" :key="t.teamId" type="button" class="ppd-cand"
                :class="{'ppd-cand--on': completeTeamId === t.teamId}" :aria-pressed="completeTeamId === t.teamId"
                @click="toggleComplete(t)">
          <v-icon size="18" :icon="completeTeamId === t.teamId ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                  :color="completeTeamId === t.teamId ? 'primary' : 'textSubtle'"/>
          <user-profile-pic :user="{imageFileName: t.players[0]?.imageFileName, fullName: t.players[0]?.fullName}" size="small"/>
          <span class="ppd-cand-name">{{ playerNames(t) }}</span>
          <span v-if="t.where" class="tl-chip ppd-chip">{{ t.where }}</span>
        </button>
      </div>
    </template>

    <!-- Partners -->
    <template v-if="needed > 0 && !completing">
      <div v-if="shortTeams.length" class="ppd-or">or make a new team</div>
      <div class="tl-label mt-4">
        {{ needed === 1 ? 'Team-mate' : `${needed} team-mates` }}
        <span class="ppd-sub"> · {{ partnerIds.length }} of {{ needed }} chosen</span>
      </div>
      <v-text-field v-model="partnerSearch" density="compact" variant="outlined" hide-details
                    prepend-inner-icon="mdi-magnify" placeholder="Search club members" class="mt-1 mb-2"/>
      <div class="ppd-cands">
        <v-skeleton-loader v-if="playersPending && !candidates.length" type="list-item-avatar@3"/>
        <button v-for="c in candidates" :key="c.id" type="button" class="ppd-cand"
                :class="{'ppd-cand--on': partnerIds.includes(c.id), 'ppd-cand--off': !partnerIds.includes(c.id) && partnerIds.length >= needed}"
                @click="togglePartner(c.id)">
          <v-icon size="18" :icon="partnerIds.includes(c.id) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline'"
                  :color="partnerIds.includes(c.id) ? 'primary' : 'textSubtle'"/>
          <user-profile-pic :user="{imageFileName: c.imageFileName, fullName: c.name}" size="small"/>
          <span class="ppd-cand-name">{{ c.name }}</span>
          <span v-if="c.mutual" class="tl-chip tl-chip--ok ppd-chip"><v-icon size="12" icon="mdi-heart" class="mr-1"/>Mutual pick</span>
          <span v-else-if="c.picked" class="tl-chip tl-chip--brand ppd-chip">Their pick</span>
          <span v-else-if="c.waiting" class="tl-chip tl-chip--warn ppd-chip">Waiting</span>
        </button>
        <div v-if="!playersPending && !candidates.length" class="ppd-empty">No club members match.</div>
      </div>
    </template>

    <!-- Where -->
    <div v-if="isAmericano && !divisions.length" class="ppd-nodiv mt-4">
      <v-icon size="18" color="primary" class="mr-2">mdi-information-outline</v-icon>
      <div class="flex-fill">
        <div class="ppd-nodiv-t">No divisions yet</div>
        <div class="ppd-hint">
          {{ waiting.length < 4
              ? `Divisions hold exactly 4, 8 or 12 players. ${waiting.length} waiting so far, ${moreToJoinText(waiting.length)} before divisions can be set up.`
              : 'Seats are given when the cycle\'s divisions are set up. Waiting players are listed for you there.' }}
        </div>
        <template v-if="waiting.length >= 4">
        <v-btn v-if="manageRoute" class="mt-2" color="primary" variant="tonal" size="small" rounded="pill"
               append-icon="mdi-chevron-right" :to="manageRoute" @click="open = false">Set up divisions</v-btn>
        <v-btn v-else-if="setupHere" class="mt-2" color="primary" variant="tonal" size="small" rounded="pill"
               append-icon="mdi-chevron-right" @click="open = false; emit('setup')">Set up divisions</v-btn>
        </template>
      </div>
    </div>
    <div v-else-if="completing" class="ppd-nodiv mt-4">
      <v-icon size="18" color="primary" class="mr-2">mdi-account-multiple-plus-outline</v-icon>
      <div class="flex-fill">
        <div class="ppd-nodiv-t">Joins {{ playerNames(completing) }}</div>
        <div class="ppd-hint">
          The team stays where it is{{ completing.where ? ` (${completing.where})` : '' }}, and keeps any matches it has already played.
        </div>
      </div>
    </div>
    <template v-else-if="isLeague || isAmericano">
      <div class="tl-label mt-4">Division</div>
      <v-select v-model="divisionId" :items="divisionItems" density="compact" variant="outlined" hide-details
                placeholder="Choose a division"/>
      <v-text-field v-if="divisionId === NEW_DIVISION" v-model="newDivisionName" class="mt-2"
                    density="compact" variant="outlined" hide-details placeholder="Division name" autofocus/>
      <div v-if="isAmericano && divisionId === NEW_DIVISION" class="ppd-hint">
        A new division has no matches until it has 4 players. Give seats in it to three more and its matches are made.
      </div>
      <template v-if="isAmericano && divisionId && divisionId !== NEW_DIVISION">
        <div class="tl-label mt-3">Taking over the seat of</div>
        <v-select v-model="outPlayerId" :items="seatItems" item-value="id" item-title="fullName"
                  density="compact" variant="outlined" hide-details
                  :placeholder="openSeats > 0 ? 'An empty seat, or a player leaving' : 'Choose the player leaving'"/>
        <div v-if="openSeats > 0" class="ppd-hint">
          This division is {{ openSeats }} short of a full rota, so {{ rowNames }} can take an empty seat. Their matches are rebuilt around them, or they take over the matches of whoever left.
        </div>
        <div v-else class="ppd-hint">Seats are fixed for the cycle. To add them without replacing anyone, start a new division or include them in the next cycle.</div>
        <div v-if="seatFeeNote" class="ppd-hint ppd-hint--note">{{ seatFeeNote }}</div>
      </template>
    </template>
    <template v-else-if="isLadder">
      <div class="tl-label mt-4">Where on the ladder</div>
      <v-select v-model="aboveLadderTeamId" :items="ladderSpots" density="compact" variant="outlined" hide-details/>
      <div class="ppd-hint">
        {{ aboveLadderTeamId
            ? 'Everyone from that spot down moves one place down.'
            : 'Joining puts people here; pick a spot to slot them in instead.' }}
      </div>
    </template>

    <!-- Extras -->
    <div v-if="!isAmericano" class="ppd-extras mt-3">
      <div v-if="playersPerTeam > 1">
        <div class="tl-label">Team name <span class="ppd-sub">(optional)</span></div>
        <v-text-field v-model="teamName" density="compact" variant="outlined" hide-details
                      :placeholder="completing ? (completing.name || `${playerNames(completing)} & ${rowNames}`) : rowNames"/>
      </div>
      <div v-if="handicapsEnabled">
        <div class="tl-label">Handicap <span class="ppd-sub">(optional)</span></div>
        <v-text-field v-model="handicap" type="number" step="any" density="compact" variant="outlined" hide-details placeholder="Scratch"/>
      </div>
    </div>

    <crud-error-display class="mt-3" :errors="actionErrors"/>

    <template #actions>
      <v-btn variant="text" @click="open = false">Cancel</v-btn>
      <v-btn v-if="!(isAmericano && !divisions.length)" color="primary" variant="flat" rounded="pill" :loading="actionPending" :disabled="!canSubmit" @click="submit">
        {{ isAmericano ? 'Give seat' : 'Place' }}
      </v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.ppd-or {
  margin-top: 14px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

.ppd-cands.ppd-cands--short {
  max-height: none;
}

.ppd-nodiv {
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgb(var(--v-theme-brandTint));
}

.ppd-nodiv-t {
  font-size: 13px;
  font-weight: 700;
}

.ppd-who {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ppd-person {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
}

.ppd-name {
  font-size: 13.5px;
  font-weight: 700;
}

.ppd-msg {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  font-style: italic;
}

.ppd-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  padding: 2px 8px;
  margin-left: auto;
  white-space: nowrap;
}

.ppd-sub {
  font-weight: 500;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: none;
  letter-spacing: 0;
}

.ppd-cands {
  max-height: 260px;
  overflow: auto;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

.ppd-cand {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &--on {
    background: rgb(var(--v-theme-brandTint));
  }

  &--off {
    opacity: 0.5;
  }
}

.ppd-cand-name {
  font-size: 13px;
  font-weight: 600;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ppd-empty {
  padding: 14px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
}

.ppd-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 6px;
}

.ppd-hint--note {
  display: block;
  color: rgb(var(--v-theme-text));
  line-height: 1.5;
}

.ppd-extras {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}
</style>
